import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Shield, LogOut, Search, Users, CreditCard, FileText, BarChart3, ArrowLeft,
  Zap, History, Mail, Send, DollarSign
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const TIERS = ['free', 'starter', 'professional', 'enterprise'];

const TIER_COLORS = {
  free: 'bg-gray-100 text-gray-800',
  starter: 'bg-blue-100 text-blue-800',
  professional: 'bg-purple-100 text-purple-800',
  enterprise: 'bg-emerald-100 text-emerald-800',
};

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState('users'); // 'users' | 'history'

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);

  // Simulate payment
  const [simEmail, setSimEmail] = useState('');
  const [simPackage, setSimPackage] = useState('starter');
  const [simIntent, setSimIntent] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [simExpanded, setSimExpanded] = useState(false);

  // Quick find
  const [quickFindText, setQuickFindText] = useState('');
  const [quickFindResult, setQuickFindResult] = useState(null);
  const [quickFinding, setQuickFinding] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access only');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchStats();
  }, [user]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (tierFilter) params.append('tier', tierFilter);
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?${params.toString()}`,
        { withCredentials: true }
      );
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`,
        { withCredentials: true }
      );
      setStats(data);
    } catch (e) { /* silent */ }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscription-changes?limit=200`,
        { withCredentials: true }
      );
      setHistory(data);
    } catch (e) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const updateTier = async (userId, newTier) => {
    setSavingId(userId);
    try {
      const { data } = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/subscription`,
        { subscription_tier: newTier, send_email: sendEmail },
        { withCredentials: true }
      );
      // Fetch the history entry to know if email actually went out
      let emailNote = '';
      if (sendEmail) {
        try {
          const hist = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscription-changes?limit=1`,
            { withCredentials: true }
          );
          if (hist.data?.[0]?.email_sent) emailNote = ` · email sent to ${data.email}`;
          else emailNote = ' · email NOT sent (check RESEND_API_KEY)';
        } catch { /* ignore */ }
      } else {
        emailNote = ' (no email)';
      }
      toast.success(`Updated to ${newTier}${emailNote}`);
      fetchUsers();
      fetchStats();
      // refresh quick-find row if visible
      if (quickFindResult) {
        setQuickFindResult(quickFindResult.map(r => r.id === userId ? data : r));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const simulatePayment = async () => {
    if (!simEmail.trim()) {
      toast.error('Enter the customer email');
      return;
    }
    setSimulating(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/simulate-payment`,
        {
          email: simEmail.trim(),
          package_id: simPackage,
          stripe_payment_intent: simIntent || undefined,
          send_email: sendEmail,
        },
        { withCredentials: true }
      );
      toast.success(`Activated ${data.user_email} → ${data.to_tier}${data.email_sent ? ' · email sent' : ''}`);
      setSimEmail('');
      setSimIntent('');
      fetchUsers();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const resendWelcome = async (u) => {
    setResendingId(u.id);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${u.id}/resend-welcome`,
        {},
        { withCredentials: true }
      );
      toast.success(`Welcome email resent to ${data.to}`);
      if (view === 'history') fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const runQuickFind = async () => {
    if (!quickFindText.trim()) {
      toast.error('Paste something containing an email address');
      return;
    }
    setQuickFinding(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/quick-find`,
        { text: quickFindText },
        { withCredentials: true }
      );
      setQuickFindResult(data);
      if (data.length === 0) {
        toast.error('No matching users in your database');
      } else {
        toast.success(`Found ${data.length} matching user${data.length > 1 ? 's' : ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Quick-find failed');
      setQuickFindResult(null);
    } finally {
      setQuickFinding(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const switchView = (v) => {
    setView(v);
    if (v === 'history') fetchHistory();
    else fetchUsers();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">ADMIN</span>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900 flex items-center" data-testid="admin-back-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Link>
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button onClick={handleLogout} className="text-gray-600 hover:text-gray-900" data-testid="admin-logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Admin · Subscriptions</h1>
          <div className="flex gap-2 bg-white rounded-lg shadow p-1">
            <button
              onClick={() => switchView('users')}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center ${view === 'users' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid="tab-users"
            >
              <Users className="w-4 h-4 mr-1.5" /> Users
            </button>
            <button
              onClick={() => switchView('history')}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center ${view === 'history' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid="tab-history"
            >
              <History className="w-4 h-4 mr-1.5" /> History
            </button>
          </div>
        </div>

        {stats && view === 'users' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={<Users className="w-7 h-7 text-blue-600" />} title="Total Users" value={stats.total_users} testid="stat-total-users" />
            <StatCard icon={<CreditCard className="w-7 h-7 text-emerald-600" />} title="Paid Users" value={stats.paid_users} testid="stat-paid-users" />
            <StatCard icon={<FileText className="w-7 h-7 text-purple-600" />} title="Total Cases" value={stats.total_cases} testid="stat-total-cases" />
            <StatCard icon={<BarChart3 className="w-7 h-7 text-orange-600" />} title="Paid Transactions" value={stats.transactions_paid} testid="stat-tx-paid" />
          </div>
        )}

        {stats?.users_by_tier && view === 'users' && (
          <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3" data-testid="tier-breakdown">
            {TIERS.map((t) => (
              <div key={t} className={`px-3 py-2 rounded-md text-sm font-medium ${TIER_COLORS[t]}`}>
                {t}: <span className="font-bold">{stats.users_by_tier[t] || 0}</span>
              </div>
            ))}
          </div>
        )}

        {view === 'users' && (
          <>
            {/* Quick Find */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow p-4 mb-6">
              <div className="flex items-center mb-2">
                <Zap className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-gray-900">Quick Find from Payment Email</h3>
                <span className="ml-2 text-xs text-gray-500">Paste a Stripe email or any text containing the customer&apos;s email</span>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <textarea
                  value={quickFindText}
                  onChange={(e) => setQuickFindText(e.target.value)}
                  placeholder="Paste Stripe payment notification email or just an email address..."
                  rows={2}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="quick-find-input"
                />
                <button
                  onClick={runQuickFind}
                  disabled={quickFinding}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                  data-testid="quick-find-btn"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  {quickFinding ? 'Finding…' : 'Find User'}
                </button>
              </div>

              {quickFindResult && quickFindResult.length > 0 && (
                <div className="mt-4 bg-white rounded-md border border-blue-200 divide-y" data-testid="quick-find-result">
                  {quickFindResult.map((u) => (
                    <div key={u.id} className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{u.name || '—'}</div>
                        <div className="text-xs text-gray-600">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${TIER_COLORS[u.subscription_tier] || 'bg-gray-100 text-gray-800'}`}>
                          {u.subscription_tier}
                        </span>
                        <select
                          value={u.subscription_tier}
                          onChange={(e) => updateTier(u.id, e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                          data-testid={`quick-find-tier-${u.email}`}
                        >
                          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button
                          onClick={() => resendWelcome(u)}
                          disabled={resendingId === u.id || u.subscription_tier === 'free'}
                          title={u.subscription_tier === 'free' ? 'Not available for free tier' : 'Resend welcome email'}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`quick-find-resend-${u.email}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" />
                          {resendingId === u.id ? '…' : 'Resend'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Simulate Payment */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg shadow p-4 mb-6" data-testid="simulate-payment-card">
              <button
                onClick={() => setSimExpanded(!simExpanded)}
                className="flex items-center w-full text-left"
                data-testid="simulate-payment-toggle"
              >
                <DollarSign className="w-5 h-5 text-emerald-600 mr-2" />
                <h3 className="font-semibold text-gray-900">Simulate Stripe Payment</h3>
                <span className="ml-2 text-xs text-gray-500">One-click activation as if Stripe fired the webhook</span>
                <span className="ml-auto text-sm text-emerald-700">{simExpanded ? '▲ Hide' : '▼ Show'}</span>
              </button>

              {simExpanded && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input
                    type="email"
                    placeholder="Customer email"
                    value={simEmail}
                    onChange={(e) => setSimEmail(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm md:col-span-2"
                    data-testid="sim-email-input"
                  />
                  <select
                    value={simPackage}
                    onChange={(e) => setSimPackage(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="sim-package-select"
                  >
                    <option value="starter">Starter · $49</option>
                    <option value="professional">Professional · $99</option>
                    <option value="enterprise">Enterprise · $199</option>
                  </select>
                  <button
                    onClick={simulatePayment}
                    disabled={simulating}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center"
                    data-testid="sim-activate-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    {simulating ? 'Activating…' : 'Activate'}
                  </button>
                  <input
                    type="text"
                    placeholder="Stripe Payment Intent ID (optional, e.g. pi_...)"
                    value={simIntent}
                    onChange={(e) => setSimIntent(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm md:col-span-4"
                    data-testid="sim-intent-input"
                  />
                  <p className="md:col-span-4 text-xs text-gray-600">
                    This creates a paid <code className="bg-white px-1 rounded">payment_transactions</code> record, upgrades the user&apos;s tier, and (if enabled below) sends the welcome email — exactly what the Stripe webhook does automatically.
                  </p>
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 flex items-center space-x-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by email or name..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="admin-user-search-input"
                  />
                </div>
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="admin-tier-filter"
                >
                  <option value="">All tiers</option>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={fetchUsers} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-testid="admin-search-btn">
                  Search
                </button>
                <button
                  onClick={() => { setQuery(''); setTierFilter(''); setTimeout(fetchUsers, 0); }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  data-testid="admin-clear-btn"
                >
                  Clear
                </button>
              </div>

              <label className="mt-3 flex items-center text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 rounded"
                  data-testid="send-email-toggle"
                />
                <Mail className="w-4 h-4 mr-1 text-blue-600" />
                Send welcome / change email to user when I update their tier
              </label>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Current Tier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Updated</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Change Tier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                    ) : users.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500" data-testid="admin-empty-row">No users found</td></tr>
                    ) : users.map((u) => (
                      <tr key={u.id} data-testid={`admin-user-row-${u.email}`}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{u.name || '—'}</div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${u.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${TIER_COLORS[u.subscription_tier] || 'bg-gray-100 text-gray-800'}`} data-testid={`current-tier-${u.email}`}>
                            {u.subscription_tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {u.subscription_updated_at ? (
                            <>
                              <div>{new Date(u.subscription_updated_at).toLocaleString()}</div>
                              <div className="text-gray-400">by {u.subscription_updated_by || '—'}</div>
                            </>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={u.subscription_tier}
                            disabled={savingId === u.id}
                            onChange={(e) => updateTier(u.id, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                            data-testid={`tier-select-${u.email}`}
                          >
                            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                          {savingId === u.id && <span className="ml-2 text-xs text-gray-500">saving...</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => resendWelcome(u)}
                            disabled={resendingId === u.id || u.subscription_tier === 'free'}
                            title={u.subscription_tier === 'free' ? 'Not available for free tier' : 'Resend welcome email for current tier'}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`resend-welcome-${u.email}`}
                          >
                            <Send className="w-3.5 h-3.5 mr-1" />
                            {resendingId === u.id ? 'Sending…' : 'Resend Welcome'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {view === 'history' && (
          <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="history-view">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h2 className="font-semibold text-gray-900 flex items-center">
                <History className="w-5 h-5 mr-2 text-blue-600" />
                Subscription Change History
              </h2>
              <p className="text-xs text-gray-500 mt-1">Last 200 tier changes</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">When</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">From → To</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Changed By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                  ) : history.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500" data-testid="history-empty">No subscription changes yet</td></tr>
                  ) : history.map((h) => (
                    <tr key={h.id} data-testid={`history-row-${h.id}`}>
                      <td className="px-4 py-3 text-xs text-gray-600">{new Date(h.changed_at).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{h.user_name || '—'}</div>
                        <div className="text-xs text-gray-500">{h.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${TIER_COLORS[h.from_tier] || 'bg-gray-100 text-gray-800'}`}>{h.from_tier}</span>
                        <span className="mx-1 text-gray-400">→</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${TIER_COLORS[h.to_tier] || 'bg-gray-100 text-gray-800'}`}>{h.to_tier}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700">{h.changed_by}</td>
                      <td className="px-4 py-3 text-xs">
                        {h.email_sent ? (
                          <span className="text-emerald-700 flex items-center"><Mail className="w-3 h-3 mr-1" /> sent</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{h.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const StatCard = ({ icon, title, value, testid }) => (
  <div className="bg-white p-4 rounded-lg shadow" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div>{icon}</div>
    </div>
  </div>
);

export default AdminPage;
